import { zipSync } from 'fflate';
import {
  PROTOCOL_VERSION,
  decodeRelayFrame,
  type JsonMessage,
  type WirePeer,
} from '../../../shared/protocol';
import type {
  DropClient,
  DropState,
  FileMeta,
  PairingCode,
  Peer,
  Profile,
  RoomInfo,
  Settings,
  ShareLink,
  TextMessage,
  Transfer,
} from './contract';
import {
  loadIdentity,
  loadPairKeys,
  saveIdentity,
  savePairKeys,
  type LocalIdentity,
} from './identity';
import { E2ECrypto, randomBase64, sha256 } from './crypto';
import {
  cleanStaleOpfs,
  clearStoredHistory,
  loadHistory,
  loadSettings,
  receivedFiles,
  saveHistory,
  saveSettings,
} from './storage';
import { ReliableSocket } from './socket';
import { RtcManager } from './rtc';
import { PeerManager } from './peers';
import { OutgoingTransferManager } from './transfer-send';
import { IncomingTransferManager } from './transfer-receive';

function clone<T>(value: T): T {
  try {
    return structuredClone(value);
  } catch {
    return JSON.parse(JSON.stringify(value)) as T;
  }
}

function makeId(prefix = 'id'): string {
  return `${prefix}-${crypto.randomUUID?.() ?? randomBase64(12)}`;
}

function queryMode(): 'relay' | 'webrtc' | 'auto' {
  const value = new URLSearchParams(location.search).get('transport');
  return value === 'relay' || value === 'webrtc' ? value : 'auto';
}

function extractRoomFromUrl(): string | undefined {
  const match = location.hash.match(/[#&?/](?:room)=([A-Za-z0-9]+)/i);
  if (match) return match[1].toUpperCase();
  const searchRoom = new URLSearchParams(location.search).get('room');
  if (searchRoom) return searchRoom.trim().toUpperCase();
  return undefined;
}

function extractPairFromUrl(): string | undefined {
  const match = location.hash.match(/[#&?/](?:pair)=([0-9]{6})/i);
  if (match) return match[1];
  const searchPair = new URLSearchParams(location.search).get('pair');
  if (searchPair) return searchPair.trim();
  return undefined;
}

async function generateThumbnail(file: File): Promise<string | undefined> {
  if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
    return undefined;
  }
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 160 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const result = canvas.toDataURL('image/jpeg', 0.65);
    return result.length <= 20_000 ? result : undefined;
  } catch {
    return undefined;
  }
}

export class DropCore implements DropClient {
  private readonly listeners = new Set<(state: DropState) => void>();
  private readonly identity: LocalIdentity;
  private readonly mode = queryMode();
  private readonly pairKeys = loadPairKeys();
  private pairHashes: string[] = [];
  private readonly pendingPair = new Map<
    string,
    { pairKey: string; resolve: () => void; reject: (error: Error) => void }
  >();
  private readonly pendingRoom = new Map<
    string,
    { resolve: (value: RoomInfo) => void; reject: (error: Error) => void }
  >();
  private readonly hostedShares = new Map<
    string,
    { files: File[]; metas: FileMeta[]; password?: string; downloads: number }
  >();
  private readonly pendingShareInfo = new Map<
    string,
    {
      resolve: (val: { files: FileMeta[]; passwordRequired: boolean }) => void;
      reject: (err: Error) => void;
    }
  >();
  private readonly pendingShareDownload = new Map<
    string,
    { resolve: () => void; reject: (err: Error) => void }
  >();
  private readonly shareTransfers = new Map<string, string>();
  private readonly peerManager = new PeerManager();
  private readonly state: DropState;
  private socket?: ReliableSocket;
  readonly rtc: RtcManager;
  private readonly outgoing: OutgoingTransferManager;
  private readonly incoming: IncomingTransferManager;
  private e2e?: E2ECrypto;
  private readonly ready: Promise<void>;
  private wakeLock?: WakeLockSentinel;

  constructor() {
    this.identity = loadIdentity();
    const initialRoom = extractRoomFromUrl();
    const roomInfo: RoomInfo | null = initialRoom
      ? { code: initialRoom, url: `${location.origin}/#room=${initialRoom}`, peers: 0 }
      : null;

    this.state = {
      status: 'connecting',
      me: clone(this.identity.profile),
      peers: [],
      transfers: loadHistory(),
      messages: [],
      room: roomInfo,
      shareLinks: [],
      settings: loadSettings(),
    };

    this.rtc = new RtcManager({
      signal: (peerId, kind, data) =>
        this.sendJson({ v: PROTOCOL_VERSION, type: 'signal', to: peerId, kind, data }),
      data: (peerId, data) => this.onPeerData(peerId, data),
      path: (peerId, path) => this.setPeerPath(peerId, path),
      closed: (peerId) => this.fallbackPeer(peerId),
    });

    this.outgoing = new OutgoingTransferManager({
      getTransfer: (id) => this.findTransfer(id),
      setTransferPath: (id, path) => this.setTransferPath(id, path),
      emit: () => this.emit(),
      persist: () => this.persist(),
      releaseWakeLock: () => this.releaseWakeLock(),
      sendControl: (peerId, msg) => this.sendControl(peerId, msg),
      encryptFor: (peerId, bytes) => this.encryptFor(peerId, bytes),
      rtc: this.rtc,
      getSocket: () => this.socket,
      mode: this.mode,
      onTransferDone: (id) => this.onOutgoingTransferDone(id),
    });

    this.incoming = new IncomingTransferManager({
      getTransfer: (id) => this.findTransfer(id),
      setTransferPath: (id, path) => this.setTransferPath(id, path),
      emit: () => this.emit(),
      persist: () => this.persist(),
      holdWakeLock: () => this.holdWakeLock(),
      releaseWakeLock: () => this.releaseWakeLock(),
      sendJson: (msg) => this.sendJson(msg),
      sendControl: (peerId, msg) => this.sendControl(peerId, msg),
      ensureRtc: (peerId, initiator) => void this.rtc.ensure(peerId, initiator),
      mode: this.mode,
    });

    this.ready = this.initialize();
    if (typeof window !== 'undefined') {
      (window as Window & { __drop?: DropClient }).__drop = this;
    }
  }

  getState(): DropState {
    return clone(this.state);
  }

  subscribe(listener: (state: DropState) => void): () => void {
    this.listeners.add(listener);
    listener(this.getState());
    return () => this.listeners.delete(listener);
  }

  updateProfile(update: Partial<Pick<Profile, 'name' | 'avatar' | 'color'>>): void {
    Object.assign(this.identity.profile, update);
    this.state.me = clone(this.identity.profile);
    saveIdentity(this.identity);
    this.emit();
    this.sendJson(this.hello());
  }

  updateSettings(update: Partial<Settings>): void {
    Object.assign(this.state.settings, update);
    saveSettings(this.state.settings);
    this.emit();
  }

  async sendFiles(peerIds: string[], files: File[]): Promise<void> {
    await this.ready;
    if (!files.length) return;

    const metas = await Promise.all(
      files.map(async (file, index) => ({
        id: makeId(`file-${index}`),
        name: file.name,
        size: file.size,
        mime: file.type || 'application/octet-stream',
        relativePath:
          (file as File & { webkitRelativePath?: string }).webkitRelativePath || undefined,
        thumbnail: await generateThumbnail(file),
      })),
    );

    await Promise.all(
      peerIds.map(async (peerId) => {
        await this.startSendTransfer(peerId, files, metas);
      }),
    );
  }

  private async startSendTransfer(
    peerId: string,
    files: File[],
    metas: FileMeta[],
  ): Promise<string> {
    const peer = this.peerManager.get(peerId);
    if (!peer?.online) {
      throw new Error(`${peer?.name ?? 'Peer'} is offline`);
    }

    const transferId = makeId('transfer');
    const transfer: Transfer = {
      id: transferId,
      peerId,
      peerName: peer.name,
      direction: 'send',
      state: 'waiting',
      files: clone(metas),
      totalBytes: metas.reduce((sum, file) => sum + file.size, 0),
      bytesDone: 0,
      bytesPerSec: 0,
      etaSec: null,
      path: 'connecting',
      startedAt: Date.now(),
    };

    this.addTransfer(transfer);
    this.outgoing.register(transferId, {
      kind: 'send',
      files,
      metas,
      accepted: false,
      acked: metas.map(() => 0),
      credits: 8 * 1024 * 1024,
      creditWaiters: [],
      sending: false,
      lastPath: undefined,
      completeSent: false,
    });

    this.sendJson({
      v: PROTOCOL_VERSION,
      type: 'transfer.offer',
      to: peerId,
      transferId,
      files: metas.map((meta, idx) => ({
        ...meta,
        lastModified: files[idx]?.lastModified,
      })),
    });

    if (this.mode !== 'relay') {
      void this.rtc.ensure(peerId, true);
    }
    if (this.mode === 'relay') {
      this.setTransferPath(transferId, 'relay');
    }

    window.setTimeout(() => {
      const current = this.findTransfer(transferId);
      if (
        current &&
        current.state !== 'done' &&
        current.state !== 'failed' &&
        current.path !== 'direct'
      ) {
        this.setTransferPath(transferId, 'relay');
      }
    }, 5_000);

    this.holdWakeLock();
    return transferId;
  }

  async sendText(peerIds: string[], text: string): Promise<void> {
    await this.ready;
    if (!text.trim()) return;

    for (const peerId of peerIds) {
      const peer = this.peerManager.get(peerId);
      if (!peer?.online) continue;

      const message: TextMessage = {
        id: makeId('message'),
        peerId,
        peerName: peer.name,
        direction: 'send',
        text,
        at: Date.now(),
      };
      this.state.messages = [message, ...this.state.messages].slice(0, 100);
      this.emit();

      this.sendJson({
        v: PROTOCOL_VERSION,
        type: 'text',
        to: peerId,
        id: message.id,
        text,
        at: message.at,
      });
    }
  }

  accept(transferId: string): void {
    this.incoming.accept(transferId);
  }

  decline(transferId: string): void {
    this.incoming.decline(transferId);
  }

  cancel(transferId: string): void {
    const transfer = this.findTransfer(transferId);
    if (!transfer) return;
    if (transfer.direction === 'receive') {
      this.incoming.cancel(transferId);
    } else {
      transfer.state = 'cancelled';
      transfer.finishedAt = Date.now();
      this.sendJson({
        v: PROTOCOL_VERSION,
        type: 'transfer.cancel',
        to: transfer.peerId,
        transferId,
      });
      this.emit();
      this.persist();
      this.releaseWakeLock();
    }
  }

  async getReceivedFiles(transferId: string): Promise<File[]> {
    return receivedFiles(transferId);
  }

  async saveReceived(
    transferId: string,
    mode: 'individual' | 'zip' | 'share',
  ): Promise<void> {
    const files = await this.getReceivedFiles(transferId);
    if (!files.length) {
      throw new Error('Received files are no longer available');
    }

    if (mode === 'share' && 'share' in navigator && navigator.canShare?.({ files })) {
      await navigator.share({ files, title: 'Drop files' });
      return;
    }

    if (mode === 'zip' || (mode === 'share' && !navigator.canShare?.({ files }))) {
      const entries: Record<string, Uint8Array> = {};
      for (const file of files) {
        entries[file.name] = new Uint8Array(await file.arrayBuffer());
      }
      const blob = new Blob([zipSync(entries)], { type: 'application/zip' });
      this.download(blob, 'drop-files.zip');
      return;
    }

    for (const file of files) {
      this.download(file, file.name);
    }
  }

  clearHistory(): void {
    const active = ['incoming', 'waiting', 'transferring', 'paused'];
    this.state.transfers = this.state.transfers.filter((t) => active.includes(t.state));
    this.state.messages = [];
    clearStoredHistory();
    this.emit();
  }

  async createPairingCode(): Promise<PairingCode> {
    await this.ready;
    const pairKey = randomBase64(32);
    const pairHash = await sha256(pairKey);
    let code = String(Math.floor(Math.random() * 1_000_000)).padStart(6, '0');
    while (this.pendingPair.has(code)) {
      code = String(Math.floor(Math.random() * 1_000_000)).padStart(6, '0');
    }

    this.pendingPair.set(code, {
      pairKey,
      resolve: () => undefined,
      reject: () => undefined,
    });
    this.sendJson({ v: PROTOCOL_VERSION, type: 'pair.create', code, pairHash });
    return {
      code,
      url: `${location.origin}/#pair=${code}`,
      expiresAt: Date.now() + 5 * 60_000,
    };
  }

  async joinPairing(code: string): Promise<void> {
    await this.ready;
    return new Promise((resolve, reject) => {
      this.pendingPair.set(`join:${code}`, { pairKey: '', resolve, reject });
      this.sendJson({ v: PROTOCOL_VERSION, type: 'pair.join', code: code.trim() });
      window.setTimeout(() => {
        const pending = this.pendingPair.get(`join:${code}`);
        if (pending) {
          this.pendingPair.delete(`join:${code}`);
          reject(new Error('Pairing code expired'));
        }
      }, 5 * 60_000);
    });
  }

  unpair(peerId: string): void {
    delete this.pairKeys[peerId];
    savePairKeys(this.pairKeys);
    void this.refreshPairHashes();
    this.peerManager.delete(peerId);
    this.refreshPeers();
  }

  setAutoAccept(peerId: string, on: boolean): void {
    this.peerManager.setAutoAccept(peerId, on);
    this.refreshPeers();
  }

  async createRoom(): Promise<RoomInfo> {
    // Fast and idempotent: return active room if one exists
    if (this.state.room) {
      return this.state.room;
    }
    await this.ready;
    if (this.state.room) {
      return this.state.room;
    }
    return this.roomRequest({ v: PROTOCOL_VERSION, type: 'room.create' });
  }

  async joinRoom(code: string): Promise<RoomInfo> {
    const sanitized = code.trim().toUpperCase();
    if (this.state.room?.code === sanitized) {
      return this.state.room;
    }
    await this.ready;
    if (this.state.room?.code === sanitized) {
      return this.state.room;
    }
    return this.roomRequest({ v: PROTOCOL_VERSION, type: 'room.join', code: sanitized });
  }

  leaveRoom(): void {
    this.state.room = null;
    this.sendJson({ v: PROTOCOL_VERSION, type: 'room.leave' });
    this.emit();
  }

  async createShareLink(files: File[], opts?: { password?: string }): Promise<ShareLink> {
    await this.ready;
    if (!files.length) throw new Error('Choose at least one file to share');

    const id = makeId('s').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 10);
    const metas: FileMeta[] = await Promise.all(
      files.map(async (file, index) => ({
        id: makeId(`sf-${index}`),
        name: file.name,
        size: file.size,
        mime: file.type || 'application/octet-stream',
        thumbnail: await generateThumbnail(file),
      })),
    );

    this.hostedShares.set(id, {
      files,
      metas,
      password: opts?.password,
      downloads: 0,
    });

    const link: ShareLink = {
      id,
      url: `${location.origin}/#s=${id}`,
      files: metas,
      downloads: 0,
      password: Boolean(opts?.password),
    };

    this.state.shareLinks = [link, ...this.state.shareLinks.filter((l) => l.id !== id)];
    this.sendJson({ v: PROTOCOL_VERSION, type: 'share.register', shareId: id });
    this.holdWakeLock();
    this.emit();
    return link;
  }

  revokeShareLink(id: string): void {
    this.hostedShares.delete(id);
    this.state.shareLinks = this.state.shareLinks.filter((l) => l.id !== id);
    this.sendJson({ v: PROTOCOL_VERSION, type: 'share.unregister', shareId: id });
    this.releaseWakeLock();
    this.emit();
  }

  joinShareLink(shareId: string): void {
    this.sendJson({ v: PROTOCOL_VERSION, type: 'share.join', shareId });
  }

  async requestShareInfo(
    shareId: string,
  ): Promise<{ files: FileMeta[]; passwordRequired: boolean }> {
    await this.ready;
    this.sendJson({ v: PROTOCOL_VERSION, type: 'share.join', shareId });

    const hostPeer = await this.waitForLinkPeer(10_000);
    if (!hostPeer) {
      throw new Error('Host is offline or share link expired');
    }

    return new Promise<{ files: FileMeta[]; passwordRequired: boolean }>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pendingShareInfo.delete(shareId);
        reject(new Error('Timed out waiting for share info'));
      }, 10_000);

      this.pendingShareInfo.set(shareId, {
        resolve: (val) => {
          clearTimeout(timer);
          resolve(val);
        },
        reject: (err) => {
          clearTimeout(timer);
          reject(err);
        },
      });

      this.sendJson({
        v: PROTOCOL_VERSION,
        type: 'share.info.request',
        to: hostPeer.id,
        shareId,
      });
    });
  }

  async requestShareDownload(shareId: string, password?: string): Promise<void> {
    await this.ready;
    const hostPeer = await this.waitForLinkPeer(10_000);
    if (!hostPeer) throw new Error('Host is offline');

    return new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pendingShareDownload.delete(shareId);
        reject(new Error('Timed out waiting for download response'));
      }, 15_000);

      this.pendingShareDownload.set(shareId, {
        resolve: () => {
          clearTimeout(timer);
          resolve();
        },
        reject: (err) => {
          clearTimeout(timer);
          reject(err);
        },
      });

      this.sendJson({
        v: PROTOCOL_VERSION,
        type: 'share.download.request',
        to: hostPeer.id,
        shareId,
        password,
      });
    });
  }

  private async waitForLinkPeer(timeoutMs: number): Promise<Peer | undefined> {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const p = this.state.peers.find((peer) => peer.sources.includes('link') && peer.online);
      if (p) return p;
      await new Promise((resolve) => window.setTimeout(resolve, 100));
    }
    return undefined;
  }

  private async initialize(): Promise<void> {
    await cleanStaleOpfs();
    this.e2e = await E2ECrypto.load(this.identity.profile.deviceId);
    await this.refreshPairHashes();

    this.socket = new ReliableSocket(
      () => this.hello(),
      (status) => {
        this.state.status = status;
        this.emit();
      },
      (value) => this.onBinary(value),
    );
    this.socket.subscribe((value) => void this.onJson(value));

    // Support automatic join on load with zero taps
    const pairCode = extractPairFromUrl();
    if (pairCode) {
      void this.joinPairing(pairCode);
    }
    const roomCode = extractRoomFromUrl();
    if (roomCode) {
      void this.joinRoom(roomCode);
    }

    window.addEventListener('hashchange', () => {
      const changedRoom = extractRoomFromUrl();
      if (changedRoom && changedRoom !== this.state.room?.code) {
        void this.joinRoom(changedRoom);
      }
      const changedPair = extractPairFromUrl();
      if (changedPair) {
        void this.joinPairing(changedPair);
      }
    });
  }

  private hello(): JsonMessage {
    return {
      v: PROTOCOL_VERSION,
      type: 'hello',
      device: { ...this.identity.profile, ecdhPublicKey: this.e2e?.publicJwk },
      pairKeyHashes: this.pairHashes,
      room: this.state.room?.code,
    };
  }

  private sendJson(value: JsonMessage): void {
    this.socket?.send(value);
  }

  private async onJson(value: JsonMessage & { from?: string }): Promise<void> {
    switch (value.type) {
      case 'welcome':
        this.rtc.setIceServers(value.iceServers);
        this.updatePeers(value.peers);
        break;
      case 'peers':
        this.updatePeers(value.peers);
        break;
      case 'peer.update':
        this.updatePeers([value.peer]);
        break;
      case 'peer.leave':
        this.peerManager.delete(value.peerId);
        this.refreshPeers();
        break;
      case 'signal':
        if (this.mode !== 'relay' && value.kind !== 'renegotiate') {
          await this.rtc.signal(value.from ?? value.to, value.kind, value.data);
        }
        break;
      case 'transfer.offer':
        this.incomingOffer(value, value.from ?? '');
        break;
      case 'transfer.accept':
        this.outgoing.handleDecision(value.transferId, true);
        break;
      case 'transfer.decline':
        this.outgoing.handleDecision(value.transferId, false);
        break;
      case 'transfer.cancel':
        this.remoteCancel(value.transferId);
        break;
      case 'transfer.ack':
        this.outgoing.handleAck(value.transferId, value.fileIndex, value.offset);
        break;
      case 'transfer.credit':
        this.outgoing.handleCredit(value.transferId, value.bytes);
        break;
      case 'transfer.resume':
        break;
      case 'transfer.complete':
        this.incoming.handleComplete(value.from ?? '', value.transferId);
        break;
      case 'text':
        this.incomingText(value, value.from ?? '');
        break;
      case 'pair.created':
        break;
      case 'pair.request':
        this.answerPair(value);
        break;
      case 'pair.result':
        this.finishPair(value);
        break;
      case 'room.result':
        this.finishRoom(value);
        break;
      case 'share.info.request':
        this.handleShareInfoRequest(value);
        break;
      case 'share.info.response':
        this.handleShareInfoResponse(value);
        break;
      case 'share.download.request':
        this.handleShareDownloadRequest(value);
        break;
      case 'share.download.response':
        this.handleShareDownloadResponse(value);
        break;
      case 'error':
        break;
      default:
        break;
    }
  }

  private updatePeers(values: WirePeer[]): void {
    this.peerManager.update(values);
    this.refreshPeers();
  }

  private refreshPeers(): void {
    this.state.peers = this.peerManager.getPublicPeers();
    this.emit();
  }

  private incomingOffer(
    value: Extract<JsonMessage, { type: 'transfer.offer' }> & { from?: string },
    peerId: string,
  ): void {
    if (!peerId) return;
    const peer = this.peerManager.get(peerId);
    if (!peer) return;

    const transfer: Transfer = {
      id: value.transferId,
      peerId,
      peerName: peer.name,
      direction: 'receive',
      state: 'incoming',
      files: value.files.map(
        ({ id, name, size, mime, relativePath, thumbnail }) => ({
          id,
          name,
          size,
          mime,
          relativePath,
          thumbnail,
        }),
      ),
      totalBytes: value.files.reduce((sum, file) => sum + file.size, 0),
      bytesDone: 0,
      bytesPerSec: 0,
      etaSec: null,
      path: 'connecting',
      startedAt: Date.now(),
    };

    this.addTransfer(transfer);
    this.incoming.register(value.transferId, {
      kind: 'receive',
      completeReceived: false,
      acked: value.files.map(() => 0),
      bytesSinceAck: value.files.map(() => 0),
    });

    if (this.mode !== 'relay') {
      void this.rtc.ensure(peerId, false);
    }
    if (
      (this.state.settings.autoAcceptPaired && peer.sources.includes('paired')) ||
      peer.sources.includes('link')
    ) {
      window.setTimeout(() => this.accept(value.transferId), 0);
    }
  }

  private setPeerPath(peerId: string, path: 'direct' | 'turn' | 'connecting'): void {
    if (this.mode === 'relay') return;
    this.peerManager.setPath(peerId, path);
    for (const transfer of this.state.transfers) {
      if (
        transfer.peerId === peerId &&
        !['done', 'failed', 'cancelled', 'declined'].includes(transfer.state)
      ) {
        transfer.path = path;
      }
    }
    this.emit();

    for (const transfer of this.state.transfers) {
      if (
        transfer.direction === 'send' &&
        transfer.peerId === peerId &&
        path === 'direct'
      ) {
        this.setTransferPath(transfer.id, 'direct');
      }
    }
  }

  private fallbackPeer(peerId: string): void {
    if (this.mode === 'webrtc') return;
    for (const transfer of this.state.transfers) {
      if (
        transfer.peerId === peerId &&
        transfer.state === 'transferring' &&
        transfer.path === 'direct'
      ) {
        transfer.state = 'paused';
        transfer.path = 'relay';
        this.emit();
        this.setTransferPath(transfer.id, 'relay');
      }
    }
  }

  private setTransferPath(transferId: string, path: 'direct' | 'relay'): void {
    const transfer = this.findTransfer(transferId);
    if (!transfer || ['done', 'failed', 'cancelled', 'declined'].includes(transfer.state)) {
      return;
    }
    transfer.path = path;
    if (transfer.state === 'paused') {
      transfer.state = 'transferring';
    }
    this.emit();

    const runtime = this.outgoing.get(transferId);
    if (runtime && runtime.lastPath === 'direct' && path === 'relay') {
      runtime.sending = false;
    }
    if (runtime) {
      void this.outgoing.pump(transferId);
    }
  }

  private onPeerData(peerId: string, data: string | ArrayBuffer): void {
    if (typeof data === 'string') {
      try {
        void this.onJson({
          ...(JSON.parse(data) as JsonMessage),
          from: peerId,
        } as JsonMessage & { from?: string });
      } catch {
        /* ignore */
      }
    } else {
      void this.incoming.onChunk(peerId, new Uint8Array(data), false);
    }
  }

  private async onBinary(data: ArrayBuffer): Promise<void> {
    const outer = decodeRelayFrame(data);
    if (!outer) return;
    try {
      const plain = await this.decryptFor(outer.peerId, outer.payload);
      await this.incoming.onChunk(outer.peerId, plain, true);
    } catch {
      /* ciphertext from an unknown peer */
    }
  }

  private remoteCancel(transferId: string): void {
    const transfer = this.findTransfer(transferId);
    if (transfer) {
      transfer.state = 'cancelled';
      transfer.finishedAt = Date.now();
      this.emit();
      this.persist();
      this.releaseWakeLock();
    }
  }

  private incomingText(
    value: Extract<JsonMessage, { type: 'text' }>,
    peerId: string,
  ): void {
    const peer = this.peerManager.get(peerId);
    if (!peer) return;
    const message: TextMessage = {
      id: value.id,
      peerId,
      peerName: peer.name,
      direction: 'receive',
      text: value.text,
      at: value.at,
    };
    this.state.messages = [message, ...this.state.messages].slice(0, 100);
    this.emit();
  }

  private sendControl(peerId: string, value: JsonMessage): void {
    const transferId =
      'transferId' in value && typeof value.transferId === 'string'
        ? value.transferId
        : undefined;
    const transfer = transferId ? this.findTransfer(transferId) : undefined;
    if (
      this.mode !== 'relay' &&
      transfer?.path === 'direct' &&
      this.rtc.send(peerId, JSON.stringify(value))
    ) {
      if (value.type !== 'transfer.complete') return;
    }
    this.sendJson(value);
  }

  private async encryptFor(peerId: string, value: Uint8Array): Promise<Uint8Array> {
    return this.e2e!.encrypt(this.peerManager.getPublicKey(peerId), value);
  }

  private async decryptFor(peerId: string, value: Uint8Array): Promise<Uint8Array> {
    return this.e2e!.decrypt(this.peerManager.getPublicKey(peerId), value);
  }

  private findTransfer(idValue: string): Transfer | undefined {
    return this.state.transfers.find((transfer) => transfer.id === idValue);
  }

  private addTransfer(transfer: Transfer): void {
    this.state.transfers = [
      transfer,
      ...this.state.transfers.filter((item) => item.id !== transfer.id),
    ];
    this.emit();
    this.persist();
  }

  private persist(): void {
    saveHistory(this.state.transfers);
  }

  private emit(): void {
    const snapshot = this.getState();
    for (const listener of this.listeners) {
      listener(snapshot);
    }
  }

  private download(blob: Blob, name: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
  }

  private holdWakeLock(): void {
    if (!('wakeLock' in navigator) || this.wakeLock) return;
    void navigator.wakeLock
      .request('screen')
      .then((lock) => {
        this.wakeLock = lock;
        lock.addEventListener('release', () => {
          this.wakeLock = undefined;
        });
      })
      .catch(() => undefined);
  }

  private releaseWakeLock(): void {
    if (this.hostedShares.size > 0) {
      return;
    }
    if (
      this.state.transfers.some(
        (transfer) =>
          transfer.state === 'transferring' || transfer.state === 'paused',
      )
    ) {
      return;
    }
    void this.wakeLock?.release();
    this.wakeLock = undefined;
  }

  private async refreshPairHashes(): Promise<void> {
    this.pairHashes = await Promise.all(
      Object.values(this.pairKeys).map((pairKey) => sha256(pairKey)),
    );
  }

  private answerPair(value: Extract<JsonMessage, { type: 'pair.request' }>): void {
    const pending = this.pendingPair.get(value.code);
    if (!pending) return;
    this.sendJson({
      v: PROTOCOL_VERSION,
      type: 'pair.key',
      to: value.requester,
      code: value.code,
      pairKey: pending.pairKey,
    });
  }

  private finishPair(value: Extract<JsonMessage, { type: 'pair.result' }>): void {
    this.pairKeys[value.peer.deviceId] = value.pairKey;
    savePairKeys(this.pairKeys);
    void this.refreshPairHashes().then(() => {
      this.sendJson(this.hello());
    });
    this.updatePeers([value.peer]);
    for (const [key, pending] of this.pendingPair) {
      if (pending.pairKey === value.pairKey || key.startsWith('join:')) {
        pending.resolve();
        this.pendingPair.delete(key);
      }
    }
  }

  private finishRoom(value: Extract<JsonMessage, { type: 'room.result' }>): void {
    if (!value.code) {
      this.state.room = null;
      this.emit();
      return;
    }
    const info: RoomInfo = {
      code: value.code,
      url: `${location.origin}/#room=${value.code}`,
      peers: value.peers.length,
    };
    this.state.room = info;
    this.pendingRoom.get(value.code)?.resolve(info);
    for (const [key, pending] of this.pendingRoom) {
      pending.resolve(info);
      this.pendingRoom.delete(key);
    }
    this.emit();
  }

  private roomRequest(
    value: Extract<JsonMessage, { type: 'room.create' | 'room.join' }>,
  ): Promise<RoomInfo> {
    const key = value.type === 'room.join' ? value.code : 'create';
    const existing = this.pendingRoom.get(key);
    if (existing) {
      return new Promise((resolve, reject) => {
        const origResolve = existing.resolve;
        const origReject = existing.reject;
        existing.resolve = (val) => {
          origResolve(val);
          resolve(val);
        };
        existing.reject = (err) => {
          origReject(err);
          reject(err);
        };
      });
    }

    return new Promise((resolve, reject) => {
      this.pendingRoom.set(key, { resolve, reject });
      this.sendJson(value);
      window.setTimeout(() => {
        const pending = this.pendingRoom.get(key);
        if (pending) {
          this.pendingRoom.delete(key);
          reject(new Error('Room request timed out'));
        }
      }, 10_000);
    });
  }

  private onOutgoingTransferDone(transferId: string): void {
    const shareId = this.shareTransfers.get(transferId);
    if (!shareId) return;
    const hosted = this.hostedShares.get(shareId);
    if (hosted) {
      hosted.downloads++;
    }
    const link = this.state.shareLinks.find((item) => item.id === shareId);
    if (link) {
      link.downloads++;
      this.emit();
    }
  }

  private handleShareInfoRequest(
    value: Extract<JsonMessage, { type: 'share.info.request' }> & { from?: string },
  ): void {
    const hosted = this.hostedShares.get(value.shareId);
    if (!hosted) {
      this.sendJson({
        v: PROTOCOL_VERSION,
        type: 'share.info.response',
        to: value.from ?? '',
        shareId: value.shareId,
        files: [],
        passwordRequired: false,
        error: 'Share not found or expired',
      });
      return;
    }

    this.sendJson({
      v: PROTOCOL_VERSION,
      type: 'share.info.response',
      to: value.from ?? '',
      shareId: value.shareId,
      files: hosted.metas,
      passwordRequired: Boolean(hosted.password),
    });
  }

  private handleShareInfoResponse(
    value: Extract<JsonMessage, { type: 'share.info.response' }> & { from?: string },
  ): void {
    const pending = this.pendingShareInfo.get(value.shareId);
    if (!pending) return;
    this.pendingShareInfo.delete(value.shareId);

    if (value.error) {
      pending.reject(new Error(value.error));
    } else {
      pending.resolve({
        files: value.files,
        passwordRequired: value.passwordRequired,
      });
    }
  }

  private async handleShareDownloadRequest(
    value: Extract<JsonMessage, { type: 'share.download.request' }> & { from?: string },
  ): Promise<void> {
    const hosted = this.hostedShares.get(value.shareId);
    if (!hosted) {
      this.sendJson({
        v: PROTOCOL_VERSION,
        type: 'share.download.response',
        to: value.from ?? '',
        shareId: value.shareId,
        error: 'Share not found or expired',
      });
      return;
    }

    if (hosted.password && hosted.password !== value.password) {
      this.sendJson({
        v: PROTOCOL_VERSION,
        type: 'share.download.response',
        to: value.from ?? '',
        shareId: value.shareId,
        error: 'Incorrect password',
      });
      return;
    }

    this.sendJson({
      v: PROTOCOL_VERSION,
      type: 'share.download.response',
      to: value.from ?? '',
      shareId: value.shareId,
    });

    const recipientPeerId = value.from ?? '';
    const transferId = await this.startSendTransfer(
      recipientPeerId,
      hosted.files,
      hosted.metas,
    );
    this.shareTransfers.set(transferId, value.shareId);
  }

  private handleShareDownloadResponse(
    value: Extract<JsonMessage, { type: 'share.download.response' }> & { from?: string },
  ): void {
    const pending = this.pendingShareDownload.get(value.shareId);
    if (!pending) return;
    this.pendingShareDownload.delete(value.shareId);

    if (value.error) {
      pending.reject(new Error(value.error));
    } else {
      pending.resolve();
    }
  }
}
